import type { MorphemeGlyphTileWordMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import runes from "@-/com.npmjs.runes2";
import { useMemo } from "react";
import { createTileSliceCache } from "#create-tile-slice-cache";
import {
  buildWordMorphemeGlyphTileSlice,
  useMorphemeGlyphTileGraphemeMorphemeSlices,
} from "#use-morpheme-glyph-tile-grapheme-morpheme-slice";

export type BatchedWordTileData = {
  /** Resolve (and cache) the tile slice for a word. Never suspends and never fires a query. */
  getSlice: (word: string) => MorphemeGlyphTileWordMorphemeSlice;
};

/**
 * The batched pinyin read a grid of WORD tiles needs, lifted ONCE for the whole set.
 *
 * A word tile that isn't handed a `morphemeGlyphTileSlice` self-resolves via a per-tile
 * `useQueries` over its characters — one TanStack observer + cache entry per character, an rAF-
 * coalesced DataLoader round-trip per newly mounted row, and a re-render per resolution. On the
 * Browse Words frequency catalog (85k words / 8 per row) that is what made freshly scrolled-in rows
 * lag several frames behind the scroll. This hook instead reads the pinyin for every unique
 * grapheme across ALL `words` in ONE content-DB query (the same `readGraphemePinyinLookup` batch the
 * Characters frequency view uses), and builds each word's slice lazily on first `getSlice(word)`.
 *
 * The latest 512 slices are memoized per resolved lookup, so nearby recycled rows cost a cache
 * hit without retaining every word ever visited. While the read is pending every grapheme resolves
 * to `null` primary pinyin — the tile
 * still draws its glyph (bundled dictionary tone color) and recolors once when the batch lands.
 *
 * `words` MUST be referentially stable (memoize it): the unique-grapheme set is derived from it.
 */
export function useBatchedWordTileData(words: readonly string[]): BatchedWordTileData {
  const uniqueGraphemes = useMemo(() => {
    const set = new Set<string>();
    for (const word of words) {
      for (const grapheme of runes(word)) {
        set.add(grapheme);
      }
    }
    return [...set];
  }, [words]);

  return useBatchedWordTileDataFromGraphemes(uniqueGraphemes);
}

/** For grouped catalogs: reuse their unique characters without retaining a flattened word array. */
export function useBatchedWordTileDataFromGraphemes(
  graphemes: readonly string[],
): BatchedWordTileData {
  const sliceByGrapheme = useMorphemeGlyphTileGraphemeMorphemeSlices(graphemes);

  return useMemo<BatchedWordTileData>(() => {
    const getPrimaryPinyin = (grapheme: string) =>
      sliceByGrapheme.get(grapheme)?.primaryPinyin ?? null;
    return {
      getSlice: createTileSliceCache((word) =>
        buildWordMorphemeGlyphTileSlice(word, getPrimaryPinyin),
      ),
    };
  }, [sliceByGrapheme]);
}
